import * as React from "react";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../misc/api/WebPortalTokenClient";
import { Column, Table } from "../Components/QueryTable";
import { ODataParams, Query, useQuery } from "../Components/Query";
import { MatchSortHeader } from "../Components/MatchSortHeader";
import { useCurrentStudyConfigurationContext } from "../Core/CurrentStudyConfigurationContext";
import DocumentTitle from "../misc/components/DocumentTitle";
import { DateMatchSortHeader } from "../Components/DateMatchSortHeader";
import { DateTimeAccuracy, LocalizedDateTime } from "../misc/components/LocalizedDateTime";
import { HamburgerMenu } from "../misc/components/HamburgerMenu";
import { useSecurityContext } from "../Core/SecurityContext";
import { Permission } from "../misc/api/apiTypes";
import { Alert, AlertNotification, Subject } from "webportalclient";
import { SortHeader } from "../Components/SortHeader";
import { uniqBy } from "../misc/ArrayFunctions";
import AsyncActionButton from "../misc/components/AsyncActionButton";
import { Option } from "../Components/MatchSortCollectionHeader";
import { Link } from "react-router-dom";

interface AlertNotificationTableProps {
    subjectOptions: Option[];
    sessionOptions: Option[];
}

const AlertNotificationTable = ({ subjectOptions, sessionOptions }: AlertNotificationTableProps) => {
    const { data, applyFilters, filters, refresh } = useQuery();
    const { currentUserPermissions } = useSecurityContext();
    const { study, fieldDisplayText } = useCurrentStudyConfigurationContext();
    const client = useWebPortalApiClient();
    const [error, setError] = React.useState<string | undefined>()
    const [availableTypes, setAvailableTypes] = React.useState([]);
    const [allNotifications, setAllNotifications] = React.useState([]);
    const [isMarkingAsRead, setIsMarkingAsRead] = React.useState(false);

    React.useEffect(() => {
        const getTypes = async () => {
            const result = await client.client.getAlertNotifications(undefined, `(subscription/studyId eq ${study.id} or subscription/id eq null) and alert/active eq true`, undefined, "subscription,alert");
            setAvailableTypes(uniqBy(result.value, (a, b) => a.subscription?.pluginName === b.subscription?.pluginName).map(a => ({
                value: a.subscription?.pluginName,
                label: a.subscription?.pluginName
            })));

            setAllNotifications(result.value);
        }
        getTypes();
    }, [study.id]);

    const clearAllFilters = () => {
        applyFilters([]);
    }

    const onMarkAsRead = async (alert: AlertNotification, isRead: boolean) => {
        await client.client.patchAlertNotification({
            isRead: isRead,
        }, alert.id);
        refresh();
    }

    const onMarkAllAsRead = async (alerts: AlertNotification[]) => {
        setIsMarkingAsRead(true);
        for (const alert of alerts.filter(a => !a.isRead)) {
            await client.client.patchAlertNotification({
                isRead: true,
            }, alert.id);
        }
        refresh();
        setIsMarkingAsRead(false);
    }

    const RenderHamburgerMenu = (alert: AlertNotification) => {
        return (
            <HamburgerMenu>
                <li>
                    {alert.isRead ?
                        <a onClick={() => onMarkAsRead(alert, false)}>Mark as Unread</a>
                        : <a onClick={() => onMarkAsRead(alert, true)}>Mark as Read</a>
                    }
                </li>
                {alert.alert.linkUri ? <li>
                    <Link to={alert.alert.linkUri} onClick={() => onMarkAsRead(alert, true)}>{alert.alert.linkText}</Link>
                </li> : null}
            </HamburgerMenu>
        );
    };

    const readUnreadOptions = [{
        value: false,
        label: "Unread",
    }, {
        value: true,
        label: "Read",
    }]

    return (
        <>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Notifications</h2>
                </div>
                <div className="col-md-6 h2">
                    <div className="pull-right">
                        <button className="btn btn-primary btn-outline" onClick={clearAllFilters}>
                            Clear All Filters
                        </button>&nbsp;
                        <AsyncActionButton isSubmitting={isMarkingAsRead} className="btn btn-primary btn-outline" onClick={() => onMarkAllAsRead(allNotifications)}>
                            Mark All as Read
                        </AsyncActionButton>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div className="panel panel-default" data-automationid="NotificationsPanelTable">
                        <div className="panel-body">
                            <Table data={data}>
                                <Column field="alert" wrapText={true} header={<SortHeader title="Headline" field="alert/headline" />} formatter={a => <><b>{a.headline}</b><p>{a.content}</p></>} />
                                <Column field="occurred" header={<DateMatchSortHeader title="Occurred" field="occurred" sortField="occurred" />} formatter={o => <LocalizedDateTime time={o} accuracy={DateTimeAccuracy.Second} isTooltip={true} />} />
                                <Column field="subscription" header={<MatchSortHeader title="Type" field="subscription/pluginname" options={availableTypes} sortField="subscription/pluginname" />} formatter={s => s?.pluginName} />
                                <Column field="alert" header={<MatchSortHeader title="Session Name" field="alert/triggeredBySession/name" options={sessionOptions} />} formatter={a => a.triggeredBySession?.name ?? "-"} />
                                <Column field="alert" header={<MatchSortHeader title={fieldDisplayText.SubjectId} field="alert/subject/number" options={subjectOptions} />} formatter={a => a.subject?.number ?? "-"} />
                                <Column field="isRead" header={<MatchSortHeader title="Read" field="isRead" options={readUnreadOptions} sortField="isRead" />} formatter={iR => iR ? "Read" : <span className="text-danger">Unread</span>} />
                                <Column field="" formatter={n => RenderHamburgerMenu(n)} />
                            </Table>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export const AlertNotificationList = () => {
    const client = useWebPortalApiClient();
    const { study } = useCurrentStudyConfigurationContext();
    const [subjects, setSubjects] = React.useState([]);
    const [sessionLabels, setSessionLabels] = React.useState([]);

    const handleRefresh = async (params: ODataParams) => {
        let filter = params.filter ? `(${params.filter}) and ` : '';
        filter += `(subscription/studyId eq ${study.id} or subscription/id eq null) and alert/active eq true`;

        const alerts = await client.client.getAlertNotifications(undefined, filter, params.orderby, "subscription,alert($select=headline,content,originatingIdentifier,triggeredBySession,linkText,linkUri,subject;$expand=triggeredBySession($select=name),subject($select=number))", params.top, params.skip, undefined, true);

        const allAlerts = await client.client.getAlertNotifications(undefined, "alert/active eq true", undefined, "alert($select=triggeredBySession,linkText,linkUri,subject;$expand=triggeredBySession($select=name),subject($select=number))")

        const sessionNames = uniqBy(allAlerts.value.map(x => x.alert?.triggeredBySession?.name).filter(x => x !== undefined).sort(), (a, b) => a === b).map(s => ({ label: s, value: s }));
        setSessionLabels(sessionNames);

        const subjectNumbers = uniqBy(allAlerts.value.map(x => x.alert?.subject?.number).filter(x => x !== undefined).sort(), (a, b) => a === b).map(s => ({ label: s, value: s }));
        setSubjects(subjectNumbers);
        return alerts;
    }

    return (
        <DocumentTitle title="Notifications | Vitalograph Web Portal">
            <Query onRefresh={handleRefresh} initialOrdering={[{ field: "isRead", condition: "asc" }]}>
                <AlertNotificationTable subjectOptions={subjects} sessionOptions={sessionLabels} />
            </Query>
        </DocumentTitle>
    );
};
